/**
 * Pathology Reading Room — the pathologist's microscopy worklist
 * (single-modality product: pathopod = digital pathology / WSI).
 *
 * Tabs: assigned (me) / unread / stat / all. Actions: open with priors,
 * sign report (Ctrl+Enter), claim next. Keyboard-first: j/k to move,
 * Enter to open, s=stat filter, u=unread, m=mine.
 */
import { createSignal, For, Show, onMount, onCleanup, createMemo } from "solid-js";
import { A } from "@solidjs/router";
import { createQuery } from "@tanstack/solid-query";
import { listStudies, type Study } from "../api/client";
import { auth } from "../auth/store";
import { modalitiesInScope, singleSpecialty } from "../auth/scope";
import { SPECIALTY_CONFIGS } from "../specialty/config";

type Tab = "mine" | "unread" | "stat" | "all";

export function PathologyReadingPage() {
  const [tab, setTab]       = createSignal<Tab>("unread");
  const [focus, setFocus]   = createSignal(0);
  const [q, setQ]           = createSignal("");

  const studies = createQuery(() => ({
    queryKey: ["studies", "reading", q()],
    queryFn:  () => listStudies(q() ? { PatientName: q() } : {}),
  }));

  const meSub = () => auth()?.claims.sub ?? "";

  // Single-modality product: the worklist is naturally scoped to
  // this product's modality codes (endoscopy = "ES").
  const scopeModalities = createMemo(() => modalitiesInScope(auth()));
  const soleSpec = createMemo(() => singleSpecialty(auth()));

  const filtered = createMemo<Study[]>(() => {
    const rows = studies.data ?? [];
    const allowed = scopeModalities();
    let scoped = allowed
      ? rows.filter((s) => s.modality && allowed.has(s.modality.toUpperCase()))
      : rows;
    // Client-side substring search across every identifying field —
    // most QIDO backends only honour PatientName, so without this
    // typing in the search box does nothing. Match is case-insensitive
    // and applied AFTER server-side filtering.
    const needle = q().trim().toLowerCase();
    if (needle) {
      scoped = scoped.filter((s) => {
        const hay = [s.patientName, s.patientId, s.accession, s.studyUid, s.description]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(needle);
      });
    }
    switch (tab()) {
      case "mine":   return scoped.filter((s) => (s.assignedTo ?? "") === meSub());
      case "unread": return scoped.filter((s) => !s.reportSigned);
      case "stat":   return scoped.filter((s) => (s.priority ?? "") === "STAT");
      default:       return scoped;
    }
  });

  onMount(() => {
    const kd = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "j") { e.preventDefault(); setFocus((i) => Math.min(filtered().length - 1, i + 1)); }
      if (e.key === "k") { e.preventDefault(); setFocus((i) => Math.max(0, i - 1)); }
      if (e.key === "m") setTab("mine");
      if (e.key === "u") setTab("unread");
      if (e.key === "s") setTab("stat");
      if (e.key === "a") setTab("all");
      if (e.key === "Enter") {
        const row = filtered()[focus()];
        if (row) window.location.href = `/study/${row.studyUid}`;
      }
    };
    window.addEventListener("keydown", kd);
    onCleanup(() => window.removeEventListener("keydown", kd));
  });

  return (
    <div class="flex h-full flex-col">
      <Show when={soleSpec()}>
        <div class="flex items-center gap-2 border-b border-brand/40 bg-brand/10 px-4 py-1.5 text-[11px]">
          <span class="chip-brand !text-[9px]">
            {SPECIALTY_CONFIGS[soleSpec()!].label.toUpperCase()}
          </span>
          <span class="text-ink-900">
            Reading-room worklist scoped to <span class="font-mono">{SPECIALTY_CONFIGS[soleSpec()!].label.toLowerCase()}</span> studies.
          </span>
        </div>
      </Show>
      <header class="flex flex-wrap items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-3">
        <span class="chip-brand">Reading room</span>
        <TabBtn current={tab()} value="mine"   label="Assigned to me" k="m" onSelect={setTab} />
        <TabBtn current={tab()} value="unread" label="Unread"         k="u" onSelect={setTab} />
        <TabBtn current={tab()} value="stat"   label="STAT"           k="s" onSelect={setTab} />
        <TabBtn current={tab()} value="all"    label="All"            k="a" onSelect={setTab} />
        <div class="min-w-[240px] flex-1">
          <input type="search"
                 placeholder="Patient / MRN / accession…"
                 class="w-full rounded border border-ink-300 bg-ink-200 px-3 py-1.5 text-sm"
                 value={q()}
                 onInput={(e) => setQ(e.currentTarget.value)} />
        </div>
        <span class="font-mono text-[11px] text-ink-700">
          {studies.isLoading ? "loading…" : `${filtered().length} studies  ·  j/k to move  ·  Enter to open`}
        </span>
      </header>

      <Show when={studies.isError}>
        <div class="m-4 rounded border border-danger/50 bg-danger/10 p-3 text-sm text-danger">
          QIDO call failed — {String(studies.error)}
        </div>
      </Show>

      <div class="flex-1 overflow-auto">
        <table class="w-full border-separate border-spacing-0 text-left text-sm">
          <thead class="sticky top-0 bg-ink-100 text-[11px] uppercase tracking-wider text-ink-700">
            <tr>
              <Th>Patient</Th>
              <Th>MRN</Th>
              <Th>Accession</Th>
              <Th>Date</Th>
              <Th>Modality</Th>
              <Th>Priority</Th>
              <Th>Report</Th>
              <Th>Description</Th>
            </tr>
          </thead>
          <tbody>
            <Show when={!studies.isLoading}
                  fallback={<tr><td colspan={8} class="p-10 text-center text-ink-700">loading…</td></tr>}>
              <For each={filtered()}>{(s, i) => (
                <tr class="group border-t border-ink-300 transition"
                    classList={{ "bg-brand/10": i() === focus(), "hover:bg-ink-200": i() !== focus() }}
                    onMouseEnter={() => setFocus(i())}>
                  <Td>
                    <A href={`/study/${s.studyUid}`} class="font-medium text-white group-hover:text-brand">
                      {s.patientName}
                    </A>
                  </Td>
                  <Td class="font-mono text-ink-700">{s.patientId}</Td>
                  <Td>{s.accession}</Td>
                  <Td>{fmtDate(s.studyDate)}</Td>
                  <Td><span class="chip-brand">{s.modality}</span></Td>
                  <Td>
                    <Show when={(s.priority ?? "ROUTINE") === "STAT"}
                          fallback={<span class="text-ink-700">routine</span>}>
                      <span class="chip-danger">STAT</span>
                    </Show>
                  </Td>
                  <Td>
                    <Show when={s.reportSigned}
                          fallback={<span class="text-ink-700">unread</span>}>
                      <span class="chip-ok">signed</span>
                    </Show>
                  </Td>
                  <Td class="truncate text-ink-700">{s.description}</Td>
                </tr>
              )}</For>
            </Show>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TabBtn(p: { current: Tab; value: Tab; label: string; k: string; onSelect: (v: Tab) => void }) {
  return (
    <button type="button"
            class="rounded border border-ink-300 px-2 py-1 text-[11px] uppercase tracking-wider"
            classList={{
              "bg-brand text-white border-brand": p.current === p.value,
              "text-ink-700 hover:text-white":    p.current !== p.value,
            }}
            onClick={() => p.onSelect(p.value)}>
      {p.label} <kbd class="ml-1 text-[10px] opacity-60">{p.k}</kbd>
    </button>
  );
}

function Th(props: { children: any }) {
  return <th class="border-b border-ink-300 px-3 py-2 text-[11px] font-semibold">{props.children}</th>;
}
function Td(props: { children: any; class?: string }) {
  return <td class={"px-3 py-2 align-middle " + (props.class ?? "")}>{props.children}</td>;
}
function fmtDate(d: string): string {
  if (!d || d.length !== 8) return d;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}
